import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosRequestConfig } from "axios";
import { BasicStateTypes, SiblingType, SuccessHistoryType } from "../types";
import { BASE_URL, getAuth, getHeaders } from "../utils/config";
import { showToast } from "../hooks/showToast";

interface StudentStateType extends BasicStateTypes {
  nameList: SiblingType[];
  successHistories: SuccessHistoryType[];
}

const initialState: StudentStateType = {
  loading: false,
  data: null,
  error: null,
  successHistories: [],
  nameList: [],
};

export interface StudentPayload {
  id?: number;
  data?: any;
  wantToShowToast?: boolean;
}

const buildUrl = (
  baseUrl: string,
  {
    id,
    action,
    pageNo,
    classGradeId,
    classRoomId,
    schoolId,
    searchKeyword,
    academicYearId,
  }: {
    id?: number;
    action: string;
    pageNo?: string | null;
    classGradeId?: string | null;
    classRoomId?: string | null;
    schoolId?: string | null;
    searchKeyword?: string | null;
    academicYearId?: number;
  }
): string => {
  const url = new URL(`${baseUrl}/students${id ? `/${id}` : ""}/${action}`);
  if (action === "all") url.searchParams.append("per_page", "20");
  if (classGradeId) url.searchParams.append("class_grade_id", classGradeId);
  if (classRoomId) url.searchParams.append("class_room_id", classRoomId);
  if (schoolId) url.searchParams.append("school_id", schoolId);
  if (searchKeyword) url.searchParams.append("search", searchKeyword);
  if (academicYearId) url.searchParams.append("academic_year_id", academicYearId.toString());
  if (pageNo) url.searchParams.append("page", pageNo);

  return url.toString();
};

export const studentCrud = createAsyncThunk(
  "student/studentCrud",
  async (
    {
      method,
      action,
      payload,
    }: { method: string; action: string; payload?: StudentPayload },
    { rejectWithValue }
  ) => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const pageNo = searchParams.get("page");
      const classGradeId = searchParams.get("class_grade_id");
      const classRoomId = searchParams.get("class_room_id");
      const schoolId = searchParams.get("school_id");
      const searchKeyword = searchParams.get("keyword");
      const defaultAcademicYear = getAuth()?.defaultAcademicYear?.id;
      const academicYearId = Number(searchParams.get("academic_year_id")) || defaultAcademicYear;
      if(action === "all" && !schoolId) return;
      const url = method.toLowerCase() === "get" ? buildUrl(BASE_URL, {
        pageNo,
        id: payload?.id,
        action,
        classGradeId,
        classRoomId,
        schoolId,
        searchKeyword,
        academicYearId,
      }) : `${BASE_URL}/students${payload?.id ? `/${payload.id}` : ""}/${action}`;

      const headers = getHeaders();
      const config: AxiosRequestConfig = {
        method,
        url,
        headers,
        data: payload?.data || {},
      };

      const response = await axios(config);
      if(!(action === "update" && payload?.wantToShowToast === false)){
        showToast(action);
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message || 'An error occurred');
    }
  }
);

export const getStudentNameOnly = createAsyncThunk(
  "student/getStudentNameOnly",
  async (
    payload: { search?: string; per_page?: number; page?: number } = {},
    { rejectWithValue }
  ) => {
    try {
      const { search, per_page, page } = payload;

      const url = new URL(`${BASE_URL}/students/all?nameonly=true`);
      if (search) url.searchParams.append("search", search);
      if (per_page) url.searchParams.append("per_page", per_page.toString());
      if (page) url.searchParams.append("page", page.toString());

      const headers = getHeaders();
      const config: AxiosRequestConfig = {
        method: "GET",
        url: url.toString(),
        headers,
      };

      const response = await axios(config);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "An error occurred"
      );
    }
  }
);


export const studentSlice = createSlice({
  name: "student",
  initialState,
  reducers: {
    replaceHistories: (state, action: PayloadAction<SuccessHistoryType[]>) => {
      state.successHistories = action.payload;
    },
    addHistory: (state, action: PayloadAction<SuccessHistoryType>) => {
      state.successHistories.push(action.payload);
    },
    removeHistory: (state, action: PayloadAction<{ id: string | number }>) => {
      state.successHistories = state.successHistories.filter(
        (history) => history.id !== action.payload.id
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(studentCrud.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(studentCrud.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(studentCrud.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getStudentNameOnly.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStudentNameOnly.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.nameList = action.payload?.data || [];
      })
      .addCase(getStudentNameOnly.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { addHistory, replaceHistories, removeHistory } = studentSlice.actions;

export default studentSlice.reducer;
